// Folders TanStack Query hooks — 消费 /api/v1/folders 与 /api/v1/messages/move。
// 列表 key 挂在 ['messages', ...] 下：已读/未读/移动/回收站等 messages 突变统一 invalidate
// ['messages'] 时，侧栏文件夹未读数跟着刷新，不用每个突变各记一遍。

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api.js';

import type { FolderDTO, FolderListResult, MoveMessagesInput } from '@aliashub/shared/dto';

export type { FolderDTO } from '@aliashub/shared/dto';

const FOLDERS_KEY = ['messages', 'folders'] as const;

export function useFolders() {
  return useQuery<FolderListResult>({
    queryKey: FOLDERS_KEY,
    queryFn: () => api.get<FolderListResult>('/api/v1/folders'),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

function useFolderMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation<TResult, Error, TVars>({
    mutationFn: fn,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['messages'] });
      // 搜索结果不在 ['messages'] 下，移动后要让结果里的文件夹标记同步
      void qc.invalidateQueries({ queryKey: ['advanced-search'] });
    },
  });
}

export function useCreateFolder() {
  return useFolderMutation((name: string) => api.post<FolderDTO>('/api/v1/folders', { name }));
}

export function useRenameFolder() {
  return useFolderMutation(({ id, name }: { id: number; name: string }) =>
    api.patch<{ id: number; name: string }>(`/api/v1/folders/${String(id)}`, { name }),
  );
}

/** 删除文件夹：其中的邮件回到收件箱 */
export function useDeleteFolder() {
  return useFolderMutation((id: number) => api.delete<{ id: number }>(`/api/v1/folders/${String(id)}`));
}

/** 批量移动；folderId=null = 移回收件箱 */
export function useMoveMessages() {
  return useFolderMutation((input: MoveMessagesInput) =>
    api.post<{ affected: number }>('/api/v1/messages/move', input),
  );
}
